import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { crmApi } from '@/api'
import type { Field } from '@/api/types/crm/crm.types'
import { isError } from '@/helpers/checkQuery'
import { useAddAlert } from '@/hooks/useAddAlert'
import debounce from '@/utils/debounce/debounce'
import {
  CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS,
  useRefetchOnBrowserTabVisible,
} from '@pages/Admin/CRM/utils/crmRealtimeWebSocket'
import { normalizeCrmFieldSelectOptions } from '@pages/Admin/CRM/utils/crmFieldSelectOptions'
import { getLeadVkLinkFormValue } from '../../../utils/leadFields'
import type { MessengerIdentityFieldUniqueName } from '../../../utils/messengerAccountFields'
import type { LeadProfileLead } from '../LeadProfileModal.types'
import {
  buildFormDataFromLead,
  getLeadProfileFieldsFingerprint,
} from './leadProfileContent.utils'

export function useLeadProfileFields(params: {
  open: boolean
  lead: LeadProfileLead | undefined
  selectedFunnelId: number | null
}) {
  const { open, lead, selectedFunnelId } = params
  const addAlert = useAddAlert()

  const [leadFields, setLeadFields] = useState<Field[]>([])
  const [isLoadingLeadFields, setIsLoadingLeadFields] = useState(false)
  const leadFieldsRequestIdRef = useRef(0)

  const leadHasFunnels =
    Array.isArray(lead?.funnels) && (lead?.funnels.length ?? 0) > 0
  const selectedFunnelIdRefForFields = useRef(selectedFunnelId)
  selectedFunnelIdRefForFields.current = selectedFunnelId
  const leadHasFunnelsRef = useRef(leadHasFunnels)
  leadHasFunnelsRef.current = leadHasFunnels
  const openRef = useRef(open)
  openRef.current = open

  useEffect(() => {
    let isCancelled = false

    if (!open) {
      setIsLoadingLeadFields(false)
      return
    }
    if (leadHasFunnels && selectedFunnelId === null) {
      setIsLoadingLeadFields(false)
      return
    }

    setIsLoadingLeadFields(true)
    const requestId = ++leadFieldsRequestIdRef.current

    crmApi
      .getAllFields(
        selectedFunnelId !== null && Number.isFinite(selectedFunnelId)
          ? { funnel_id: selectedFunnelId }
          : undefined,
      )
      .then((res) => {
        if (isCancelled || requestId !== leadFieldsRequestIdRef.current) return
        if (isError(res)) throw new Error()
        setLeadFields(res?.data?.data ?? [])
      })
      .catch(() => {
        if (isCancelled || requestId !== leadFieldsRequestIdRef.current) return
        addAlert({
          title: 'Ошибка',
          text: 'Ошибка загрузки полей для выбранной воронки',
          type: 'error',
        })
      })
      .finally(() => {
        if (isCancelled || requestId !== leadFieldsRequestIdRef.current) return
        setIsLoadingLeadFields(false)
      })

    return () => {
      isCancelled = true
    }
  }, [open, selectedFunnelId, leadHasFunnels, addAlert])

  const debouncedLeadFieldsVisibleRefetchRef = useRef(
    debounce(() => {
      if (!openRef.current) return
      if (
        leadHasFunnelsRef.current &&
        selectedFunnelIdRefForFields.current === null
      ) {
        return
      }

      const funnelId = selectedFunnelIdRefForFields.current
      const requestId = ++leadFieldsRequestIdRef.current

      crmApi
        .getAllFields(
          funnelId !== null && Number.isFinite(funnelId)
            ? { funnel_id: funnelId }
            : undefined,
        )
        .then((res) => {
          if (requestId !== leadFieldsRequestIdRef.current) return
          if (isError(res)) return

          const nextFields = res?.data?.data ?? []
          setLeadFields((prev) => {
            if (!prev.length) return nextFields

            return nextFields.map((field) => {
              if (field.type !== 'select') return field
              const nextOptions = normalizeCrmFieldSelectOptions(
                field.value_options,
              )
              if (nextOptions.length) return field

              const prevField = prev.find(
                (item) =>
                  item.id === field.id ||
                  item.unique_name === field.unique_name,
              )
              const prevOptions = normalizeCrmFieldSelectOptions(
                prevField?.value_options,
              )
              if (!prevOptions.length) return field

              return { ...field, value_options: prevOptions }
            })
          })
        })
        .catch(() => {
          // тихий refetch — без алертов и без лоадера
        })
    }, CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS),
  )

  useRefetchOnBrowserTabVisible(() => {
    debouncedLeadFieldsVisibleRefetchRef.current()
  }, open)

  const [formData, setFormData] = useState<Record<string, string>>({})
  const [identityFieldErrors, setIdentityFieldErrors] = useState<
    Partial<Record<MessengerIdentityFieldUniqueName, string>>
  >({})

  const leadProfileFieldsFingerprint = useMemo(
    () => getLeadProfileFieldsFingerprint(lead),
    [lead?.fields],
  )
  const leadVkLinkForForm = getLeadVkLinkFormValue(lead)

  useEffect(() => {
    setFormData(buildFormDataFromLead(lead))
  }, [lead?.id, leadProfileFieldsFingerprint, leadVkLinkForForm])

  const handleChangeFieldData = useCallback(
    (uniqueName: string, value: string) => {
      setFormData((prev) => ({
        ...prev,
        [uniqueName]: value,
      }))
    },
    [],
  )

  const handleClearIdentityFieldError = useCallback(
    (uniqueName: MessengerIdentityFieldUniqueName) => {
      setIdentityFieldErrors((prev) => {
        if (!prev[uniqueName]) {
          return prev
        }

        const next = { ...prev }
        delete next[uniqueName]
        return next
      })
    },
    [],
  )

  return {
    leadFields,
    isLoadingLeadFields,
    formData,
    setFormData,
    identityFieldErrors,
    setIdentityFieldErrors,
    handleChangeFieldData,
    handleClearIdentityFieldError,
    buildFormDataFromLead,
  }
}
